import { useClientContext, toScopeAccountId } from './useClientContext';

export interface LessonClientScope {
  /** The active client account id, or null in Standard (base) context. */
  clientAccountId: number | null;
  /** Whether a client-account context is active. */
  isClientContext: boolean;
  /** Active client retained for the focused metadata resource in every locale. */
  metadataAccountId: number | null;
  /** Whether metadata is scoped to a client, including non-English locales. */
  isMetadataClientContext: boolean;
  /**
   * Stable key for the editor's content hydration / autosave baseline. Switching
   * lesson, locale, or client account changes it so the store reseeds and the
   * autosave baseline resets (preventing a cross-account dirty save).
   */
  hydrationScopeKey: string;
}

/**
 * Derives the lesson editor's client-context scope from the active client
 * context and selected locale. Client identity is retained in every locale so
 * block translation projections, metadata, preview, and hydration all share
 * one account scope.
 */
export function useLessonClientScope(
  lessonId: string | undefined,
  selectedLocale: string
): LessonClientScope {
  const { clientContext } = useClientContext();
  const metadataAccountId = toScopeAccountId(clientContext?.id);
  const clientAccountId = metadataAccountId;
  return {
    clientAccountId,
    isClientContext: clientAccountId !== null,
    metadataAccountId,
    isMetadataClientContext: metadataAccountId !== null,
    hydrationScopeKey: `${lessonId ?? ''}:${selectedLocale}:${clientAccountId ?? 'base'}`,
  };
}
